// Voucher Service for Frankenfloss Booking System
// Handles voucher purchase, validation, extension and admin operations

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5174/api';

// ===== Types =====

export interface PurchaseVoucherRequest {
  amount: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  paymentMethod: 'paypal' | 'banktransfer' | 'stripe' | 'klarna';
}

export interface PurchaseVoucherResponse {
  voucherId: number;
  voucherCode: string;
  amount: number;
  paymentStatus: string;
  paymentMethod: string;
  bankTransferReference?: string;
  bankTransferInstructions?: string;
}

export interface ValidateVoucherResponse {
  exists: boolean;
  isValid: boolean;
  message: string;
  code?: string;
  remainingAmount?: number;
  originalAmount?: number;
  validUntil?: string;
  daysUntilExpiration?: number;
  status?: string;
}

export interface PayPalOrderResponse {
  voucherId: number;
  amount: number;
  currency: string;
  payPalOrderId?: string;
  approvalUrl?: string;
  status: string;
  message: string;
}

export interface PayPalCaptureResponse {
  success: boolean;
  voucherId: number;
  voucherCode: string;
  amount: number;
  status: string;
  message: string;
}

export interface VoucherListItem {
  id: number;
  code: string;
  originalAmount: number;
  remainingAmount: number;
  status: string;
  paymentStatus: string;
  buyerName: string;
  buyerEmail: string;
  recipientName?: string;
  recipientEmail?: string;
  validUntil?: string;
  createdAt: string;
  issuedAt?: string;
  redemptions?: VoucherRedemption[];
}

export interface VoucherRedemption {
  id: number;
  bookingId: number;
  amountRedeemed: number;
  remainingAfter: number;
  redeemedAt: string;
  notes?: string;
}

export interface VoucherStatistics {
  totalVouchers: number;
  activeVouchers: number;
  partiallyRedeemedVouchers: number;
  fullyRedeemedVouchers: number;
  expiredVouchers: number;
  cancelledVouchers: number;
  pendingPaymentVouchers: number;
  totalValueSold: number;
  totalValueRedeemed: number;
  totalOutstandingValue: number;
  totalExpiredValue: number;
  vouchersExpiringSoon: number;
  valueExpiringSoon: number;
  vouchersSoldThisMonth: number;
  valueSoldThisMonth: number;
  redemptionsThisMonth: number;
  valueRedeemedThisMonth: number;
}

export interface VoucherFilterParams {
  code?: string;
  buyerEmail?: string;
  statuses?: string[];
  paymentStatus?: string;
  isExpired?: boolean;
  expiringWithinDays?: number;
  minAmount?: number;
  maxAmount?: number;
  createdAfter?: string;
  createdBefore?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface AdminCreateVoucherRequest {
  amount: number;
  buyerName: string;
  buyerEmail: string;
  buyerPhone?: string;
  recipientName?: string;
  recipientEmail?: string;
  personalMessage?: string;
  skipPayment?: boolean;
}

// ===== Helper Functions =====

function getAuthToken(): string | null {
  return localStorage.getItem('authToken');
}

async function apiRequest<T>(
  method: string,
  endpoint: string,
  data?: any
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status} ${response.statusText}`);
  }

  return response.json();
}

// ===== Customer API =====

export const voucherService = {
  /**
   * Purchase a new voucher (customer)
   */
  async purchaseVoucher(request: PurchaseVoucherRequest): Promise<PurchaseVoucherResponse> {
    return apiRequest<PurchaseVoucherResponse>('POST', '/vouchers/purchase', request);
  },

  /**
   * Validate a voucher code (public)
   */
  async validateVoucher(code: string): Promise<ValidateVoucherResponse> {
    return apiRequest<ValidateVoucherResponse>('GET', `/vouchers/validate/${encodeURIComponent(code)}`);
  },

  // ===== PayPal Integration =====

  /**
   * Create PayPal order for voucher purchase
   */
  async createPayPalOrder(
    voucherId: number,
    returnUrl?: string,
    cancelUrl?: string
  ): Promise<PayPalOrderResponse> {
    return apiRequest<PayPalOrderResponse>('POST', '/vouchers/paypal/create-order', {
      voucherId,
      returnUrl,
      cancelUrl,
    });
  },

  /**
   * Capture PayPal order after customer approval
   */
  async capturePayPalOrder(
    voucherId: number,
    payPalOrderId: string
  ): Promise<PayPalCaptureResponse> {
    return apiRequest<PayPalCaptureResponse>('POST', '/vouchers/paypal/capture-order', {
      voucherId,
      payPalOrderId,
    });
  },
};

// ===== Admin API =====

export const adminVoucherService = {
  /**
   * Get list of vouchers with filtering (Admin)
   */
  async getVouchers(params?: VoucherFilterParams): Promise<{ items: VoucherListItem[]; totalCount: number }> {
    const queryParams = new URLSearchParams();

    if (params) {
      if (params.code) queryParams.append('code', params.code);
      if (params.buyerEmail) queryParams.append('buyerEmail', params.buyerEmail);
      if (params.statuses?.length) queryParams.append('statuses', params.statuses.join(','));
      if (params.paymentStatus) queryParams.append('paymentStatus', params.paymentStatus);
      if (params.isExpired !== undefined) queryParams.append('isExpired', String(params.isExpired));
      if (params.expiringWithinDays) queryParams.append('expiringWithinDays', String(params.expiringWithinDays));
      if (params.minAmount) queryParams.append('minAmount', String(params.minAmount));
      if (params.maxAmount) queryParams.append('maxAmount', String(params.maxAmount));
      if (params.createdAfter) queryParams.append('createdAfter', params.createdAfter);
      if (params.createdBefore) queryParams.append('createdBefore', params.createdBefore);
      if (params.page) queryParams.append('page', String(params.page));
      if (params.pageSize) queryParams.append('pageSize', String(params.pageSize));
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDescending !== undefined) queryParams.append('sortDirection', params.sortDescending ? 'desc' : 'asc');
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/admin/vouchers?${queryString}` : '/admin/vouchers';

    return apiRequest<{ items: VoucherListItem[]; totalCount: number }>('GET', endpoint);
  },

  /**
   * Get voucher by ID (Admin)
   */
  async getVoucher(id: number): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('GET', `/admin/vouchers/${id}`);
  },

  /**
   * Create voucher manually (Admin)
   */
  async createVoucher(request: AdminCreateVoucherRequest): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('POST', '/admin/vouchers', request);
  },

  /**
   * Cancel a voucher (Admin)
   */
  async cancelVoucher(id: number, reason: string): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('PUT', `/admin/vouchers/${id}/cancel`, { reason });
  },

  /**
   * Confirm bank transfer payment (Admin)
   */
  async confirmPayment(id: number, paymentReference: string): Promise<VoucherListItem> {
    return apiRequest<VoucherListItem>('PUT', `/admin/vouchers/${id}/confirm-payment`, { paymentReference });
  },

  /**
   * Get voucher statistics (Admin)
   */
  async getStatistics(): Promise<VoucherStatistics> {
    return apiRequest<VoucherStatistics>('GET', '/admin/vouchers/statistics');
  },
};

// ===== Predefined Amounts =====

export const VOUCHER_AMOUNTS = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];

export const MIN_CUSTOM_AMOUNT = 25;
export const MAX_CUSTOM_AMOUNT = 1000;

// ===== Status Helpers =====

export const VOUCHER_STATUS_LABELS: Record<string, string> = {
  PendingPayment: 'Zahlung ausstehend',
  Active: 'Aktiv',
  PartiallyRedeemed: 'Teilweise eingelöst',
  FullyRedeemed: 'Vollständig eingelöst',
  Expired: 'Abgelaufen',
  Cancelled: 'Storniert',
  Transferred: 'Übertragen',
};

export const VOUCHER_STATUS_COLORS: Record<string, string> = {
  PendingPayment: 'orange',
  Active: 'green',
  PartiallyRedeemed: 'blue',
  FullyRedeemed: 'gray',
  Expired: 'red',
  Cancelled: 'red',
  Transferred: 'purple',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  Pending: 'Ausstehend',
  Paid: 'Bezahlt',
  Failed: 'Fehlgeschlagen',
  Refunded: 'Erstattet',
};

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default voucherService;
